import {
  TableContainer,
  Box,
  Typography,
  TableCell,
  TableHead,
  TableRow,
  Table,
  TableBody,
  Stack,
} from '@mui/material';
import { FC } from 'react';
import utils from 'utils';
import styles from './TableView.styles';

type TableAvaliationSection = {
  name: string;
  headers: string[];
  type: string[];
  values: string[][];
};

type TableAvaliation = TableAvaliationSection[];

type Props = {
  data: TableAvaliation;
  comp?: {
    cz?: {
      name: string;
      val: { [key: string]: string };
    }[];
  };
  compact?: boolean;
};

const FLAT_TYPES_WHEN_EQUAL = true;
const BASE_FONT_SIZE = '9px';
const BODY_CELL_PADDING_X = '8px';
const BASE_LINE_WIDTH = '80px';
const HEADER_PADDING = '4px';
const HEADER_COMPACT_PADDING = '2px 8px';
const BODY_ROW_HEIGHT = '25px';
const BODY_CELL_BORDER = '3px solid white';

const normalize = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

const isProgenieSection = (name = '') =>
  normalize(name).includes('PROGENIE');

const isRebLabel = (label = '') => normalize(label).includes('REB');
const isFilhosLabel = (label = '') => normalize(label).includes('FILHO');

type FooterValues = {
  REB?: string;
  FILHOS?: string;
  REB2?: string;
  FILHOS2?: string;
};

const getFlatValues = (values: any): string[] => {
  if (!Array.isArray(values)) return [];
  if (Array.isArray(values[0])) {
    return (values[0] as any[]).map((v) => `${v ?? ''}`);
  }
  return (values as any[]).map((v) => `${v ?? ''}`);
};

const getFooterFromProgenieSection = (
  section?: TableAvaliationSection
): FooterValues | null => {
  if (!section) return null;

  const labels = [ ...(section.type || []), ...(section.headers || []) ];
  const values = getFlatValues(section.values);
  if (values.length === 0) return null;

  const rebIndexes = labels
    .map((label, index) => (isRebLabel(label) ? index : -1))
    .filter((index) => index >= 0);
  const filhosIndexes = labels
    .map((label, index) => (isFilhosLabel(label) ? index : -1))
    .filter((index) => index >= 0);

  const reb = rebIndexes[0] != null ? values[rebIndexes[0]] : values[0];
  const filhos = filhosIndexes[0] != null ? values[filhosIndexes[0]] : values[1];
  const reb2 =
    rebIndexes[1] != null ? values[rebIndexes[1]] : values[2];
  const filhos2 =
    filhosIndexes[1] != null ? values[filhosIndexes[1]] : values[3];

  if (reb == null && filhos == null && reb2 == null && filhos2 == null) {
    return null;
  }

  return {
    REB: reb,
    FILHOS: filhos,
    REB2: reb2,
    FILHOS2: filhos2,
  };
};

const getFooterFromComp = (
  cz?: {
    name: string;
    val: { [key: string]: string };
  }[]
): FooterValues | null => {
  if (!Array.isArray(cz) || cz.length === 0) return null;

  const target = cz.find((entry) => {
    const keys = Object.keys(entry?.val || {});
    return keys.some((key) => isRebLabel(key) || isFilhosLabel(key));
  });
  if (!target?.val) return null;

  const pairs = Object.entries(target.val).reduce(
    (acm, [ key, value ]) => {
      if (isRebLabel(key)) acm.reb.push(value);
      if (isFilhosLabel(key)) acm.filhos.push(value);
      return acm;
    },
    { reb: [] as string[], filhos: [] as string[] }
  );

  return {
    REB: pairs.reb[0],
    FILHOS: pairs.filhos[0],
    REB2: pairs.reb[1],
    FILHOS2: pairs.filhos[1],
  };
};

const TableView: FC<Props> = ({ data, comp, compact = false }) => {
  const filteredData = (data || []).filter((entry) => !isProgenieSection(entry?.name));
  const progenieFooter =
    getFooterFromProgenieSection(
      (data || []).find((entry) => isProgenieSection(entry?.name))
    ) || getFooterFromComp(comp?.cz);

  const uniqueTypes = filteredData.reduce((acm: boolean, d, _, arr) => {
    if (!acm) return acm;
    return (arr[0].type || []).every((e) => d.type.includes(e));
  }, true);

  const footerLine = progenieFooter
    ? [
      `N° de rebanhos: ${progenieFooter.REB || '-'}`,
      `N° de filhos: ${progenieFooter.FILHOS || '-'}`,
      progenieFooter.REB2 != null && progenieFooter.REB2 !== ''
        ? `N° de rebanhos: ${progenieFooter.REB2 || '-'}`
        : null,
      progenieFooter.FILHOS2 != null && progenieFooter.FILHOS2 !== ''
        ? `N° de filhos: ${progenieFooter.FILHOS2 || '-'}`
        : null,
    ]
      .filter(Boolean)
      .join(' | ')
    : null;
  

  return (
    <Stack {...utils.stack.flex.col('flex-start', 'flex-start')} sx={{ width: '100%' }}>
      <Stack
        {...utils.stack.flex.row('flex-start', 'flex-start')}
        sx={{ width: '100%', overflowX: 'auto' }}
      >
        {filteredData.map((avaliation, J) => {
          const ONLY_VALUES=avaliation.type?.length<=0 && avaliation.headers?.length<=0;
          return (
            <TableContainer sx={styles.tableContainer} key={J}>
              <Box
                sx={{ width: '100%', ...utils.sx.flex.col('center', 'center') }}
              >
                <Typography variant="h3" sx={{ ...styles.tableSectionHeader, fontSize: '0.575rem !important' }}>
                  {avaliation.name}
                </Typography>
              </Box>
              <Table
                sx={{
                  minWidth: compact ? 'auto' : '150px',
                  border: 'none !important',
                  tableLayout: compact ? 'auto' : undefined,
                }}
              >
                <TableHead>
                  <TableRow>
                    {(!ONLY_VALUES && (J === 0 ||
                      (J > 0 && (!FLAT_TYPES_WHEN_EQUAL || !uniqueTypes)))) && (
                      <TableCell></TableCell>
                    )}
                    {avaliation.headers.map((header) => (
                      <TableCell
                        sx={{
                          ...styles.tableCellHeader,
                          fontSize: BASE_FONT_SIZE,
                          padding: compact
                            ? HEADER_COMPACT_PADDING
                            : HEADER_PADDING,
                          //whiteSpace: compact ? 'nowrap' : 'inherit',
                          whiteSpace: 'nowrap',
                          minWidth: 40,
                        }}
                        key={header}
                        align="center"
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ONLY_VALUES && avaliation.values.map((row, index) => (
                    <TableCell
                      sx={{
                        ...styles.tableCellValue,
                        fontSize: BASE_FONT_SIZE,
                        padding: `0 ${BODY_CELL_PADDING_X}`,
                        whiteSpace: compact ? 'nowrap' : 'inherit',
                        width: compact ? '100px' : BASE_LINE_WIDTH,
                        height: BODY_ROW_HEIGHT,
                        lineHeight: BODY_ROW_HEIGHT,
                        border: BODY_CELL_BORDER,
                      }}
                      align="right"
                      key={index}
                    >
                      {row}
                    </TableCell>
                  ))}
                  {avaliation.type.map((type, i, arr) => [
                    <TableRow
                      key={i}
                      sx={{
                        '&:last-child th': { border: 0 },
                        height: BODY_ROW_HEIGHT,
                        maxHeight: BODY_ROW_HEIGHT,
                      }}
                    >
                      {(J === 0 ||
                        (J > 0 && (!FLAT_TYPES_WHEN_EQUAL || !uniqueTypes))) && (
                        <TableCell
                          align="center"
                          sx={{
                            ...styles.tableLineType,
                            fontSize: BASE_FONT_SIZE,
                            padding: `0 ${BODY_CELL_PADDING_X}`,
                            whiteSpace: compact ? 'nowrap' : 'inherit',
                            //width: compact ? '60px' : BASE_LINE_WIDTH,
                            height: BODY_ROW_HEIGHT,
                            lineHeight: BODY_ROW_HEIGHT,
                            border: BODY_CELL_BORDER,
                          }}
                        >
                          {type}
                        </TableCell>
                      )}
                      {avaliation.values.map((row) => (
                        <TableCell
                          sx={{
                            ...styles.tableCellValue,
                            fontSize: BASE_FONT_SIZE,
                            padding: `0 ${BODY_CELL_PADDING_X}`,
                            whiteSpace: compact ? 'nowrap' : 'inherit',
                            height: BODY_ROW_HEIGHT,
                            lineHeight: BODY_ROW_HEIGHT,
                            border: BODY_CELL_BORDER,
                          }}
                          align="right"
                          key={row[i]}
                        >
                          {row[i]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ])}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })}
      </Stack>
      {footerLine && (
        <Typography variant="body2" sx={{ fontSize: BASE_FONT_SIZE, padding: '6px 2px 0' }}>
          {footerLine}
        </Typography>
      )}
    </Stack>
  );
};

export default TableView;
